import { Alert } from '@mui/material';
import { useMemo } from 'react';
import { useOutletContext } from 'react-router';
import Tabs from '../../../../components/Tabs';
import { useLanguage } from '../../../../contexts/language';
import { CrewTemplateSubPageContext } from '../types';
import CertificationTabContent from './CertificationTabContent';
import useFreshDataEnsurer from '../../../../hooks/useFreshDataEnsurer';
import useDraftList from '../../../../hooks/useDraftList';
import { useNavigationGuardBlocker } from '../../../../hooks/useNavigationGuard';
import {
  CrewMemberCertificateStatus,
  CrewMemberRole,
} from '../../../../types/core';

const CrewCertificationsPage = () => {
  const { crewMember, certificatesManagement, refetch } =
    useOutletContext<CrewTemplateSubPageContext>();
  const {
    crewMemberCertificates,
    refetch: refetchCertificates,
    isFetching: isFetchingCertificates,
  } = certificatesManagement;
  const { t } = useLanguage();

  const areCertificatesFresh = useFreshDataEnsurer(
    isFetchingCertificates,
    refetchCertificates,
  );

  const { list, isReady, operations } = useDraftList(
    crewMemberCertificates,
    areCertificatesFresh,
  );

  const pendingCertifications = useMemo(
    () =>
      list.filter(
        item =>
          !item.isDraft &&
          item.data.status === CrewMemberCertificateStatus.PendingApproval,
      ),
    [list],
  );

  useNavigationGuardBlocker(() => Boolean(list.find(item => item.isDraft)));

  return (
    <>
      <Tabs
        tabs={[
          { label: t('content.all') },
          { label: t('content.pending-approval') },
        ]}
        variant="buttons"
      >
        <CertificationTabContent
          crewMember={crewMember}
          draftListManager={{ list, isReady, operations }}
          emptyContent={
            crewMember.crewMemberRole === CrewMemberRole.Skipper && (
              <Alert severity="error" className="mb-4 shadow-iots">
                {t('validation.valid-certifications-minimum-button')}
              </Alert>
            )
          }
          showAddButton
          areCertificatesPending={!areCertificatesFresh}
          isFetchingCertificates={isFetchingCertificates}
          onCertificatesDataUpdate={() => {
            refetch();
            refetchCertificates();
          }}
        />
        <CertificationTabContent
          crewMember={crewMember}
          draftListManager={{
            list: pendingCertifications,
            isReady,
            operations,
          }}
          emptyContent={
            <div className="grid place-items-center w-full grow">
              <h1 className="text-2xl font-bold">
                {t('content.no-certifications-found')}
              </h1>
            </div>
          }
          areCertificatesPending={!areCertificatesFresh}
          isFetchingCertificates={isFetchingCertificates}
          onCertificatesDataUpdate={() => {
            refetch();
            refetchCertificates();
          }}
        />
      </Tabs>
    </>
  );
};

export default CrewCertificationsPage;
